/**
 * Array-based handler chain implementation
 * Much simpler than linked list approach
 */

import { EventEmitter } from '../base/event-emitter.js';
import type {
  IAsyncHandler,
  ISyncHandler,
} from '../interfaces/handler.interface.js';
import type { TranslationData } from '../types/translation-data.types.js';
import type {
  TranslationContext,
  TranslationRequest,
  TranslationResult,
} from '../types/translation.types.js';
import { logger } from '../utils/logger.js';

export class HandlerChain {
  private asyncHandlers: IAsyncHandler[] = [];
  private syncHandlers: ISyncHandler[] = [];
  private bus: EventEmitter;

  constructor(eventEmitter: EventEmitter) {
    this.bus = eventEmitter;
  }

  addSyncHandler(handler: ISyncHandler): void {
    if (!handler.enabled) return;
    this.syncHandlers.push(handler);
    this.sortHandlersIfNeeded(this.syncHandlers);
    logger.debug(`Sync handler added: ${handler.name}`);
  }

  handleSync(request: TranslationRequest): TranslationResult | null {
    for (const handler of this.syncHandlers) {
      try {
        const result = handler.lookup(request);
        if (result !== null && result.value) {
          logger.debug(`Sync hit: ${handler.name} (${result.duration}ms)`);
          return result;
        }
      } catch (error) {
        logger.warn(`Sync lookup failed in ${handler.name}:`, error);
        const repoBus = this.bus.repo(request, handler.name);
        repoBus.error(error as Error);
        continue;
      }
    }
    return null;
  }

  private saveToMemoryHandlers(
    request: TranslationRequest,
    result: TranslationResult,
    _sourceHandler: IAsyncHandler
  ): void {
    const memoryHandlers = this.syncHandlers;
    if (memoryHandlers.length === 0) return;
    logger.debug(`Saving to ${memoryHandlers.length} memory handlers`);

    // Result already contains value and styles from source handler
    memoryHandlers.forEach(handler => {
      try {
        handler.save(request, result);
        logger.debug(`Saved to memory: ${handler.name}`);
      } catch (error) {
        logger.warn(`Failed to save to memory ${handler.name}:`, error);
        const repoBus = this.bus.repo(request, handler.name);
        repoBus.error(error as Error);
      }
    });
  }

  private saveNamespaceToMemoryHandlers(
    translations: TranslationData,
    context: TranslationContext
  ): void {
    const memoryHandlers = this.syncHandlers.filter(
      h => h.type === 'memory' && h.saveNamespace
    );

    if (memoryHandlers.length === 0) return;

    logger.debug(
      `Saving namespace to ${memoryHandlers.length} memory handlers`
    );

    // ✅ Save to memory handlers (sync)
    memoryHandlers.forEach(handler => {
      try {
        handler.saveNamespace!(translations, context);
        logger.debug(`Namespace saved to memory: ${handler.name}`, {
          locale: context.locale,
          namespace: context.namespace,
        });
      } catch (error) {
        logger.warn(
          `Failed to save namespace to memory ${handler.name}:`,
          error
        );
        const repoBus = this.bus.repo(
          `${context.locale}:${context.namespace}`,
          handler.name
        );
        repoBus.error(error as Error);
      }
    });
  }

  addAsyncHandler(handler: IAsyncHandler): void {
    if (!handler.enabled) return;
    this.asyncHandlers.push(handler);
    this.sortHandlersIfNeeded(this.asyncHandlers);

    logger.debug(`Async handler added: ${handler.name}`);
  }

  async handleAsync(
    request: TranslationRequest
  ): Promise<TranslationResult | null> {
    for (const handler of this.asyncHandlers) {
      try {
        logger.debug(`Trying handler: ${handler.name}`);
        const result = await handler.lookup(request);

        // Check if we got a successful result (not fallback)
        if (result.value && result.status === 200) {
          // Found translation - save to all storage handlers that come before this one
          logger.debug(`Async hit: ${handler.name} (${result.duration}ms)`);

          // If a full namespace was loaded during lookup, save it first
          if (result.namespaceLoaded) {
            logger.debug(
              `Saving loaded namespace: ${result.namespaceLoaded.locale}:${result.namespaceLoaded.namespace}`
            );
            await this.saveNamespaceToHandlers(result.namespaceLoaded.data, {
              locale: result.namespaceLoaded.locale,
              namespace: result.namespaceLoaded.namespace,
            });
            return result;
          }

          // Save to storage and memory handlers (styles included in result)
          await this.saveToStorageHandlers(request, result, handler);
          this.saveToMemoryHandlers(request, result, handler);

          return result;
        }

        // If we got a fallback result (404, 423, error), continue to next handler
        logger.debug(
          `Handler ${handler.name} returned fallback (status: ${result.status})`
        );
      } catch (error) {
        logger.warn(`Handler ${handler.name} failed:`, error);
        const repoBus = this.bus.repo(request, handler.name);
        repoBus.error(error as Error);
        continue;
      }
    }
    logger.debug(`No async handlers found translation for ${request.cleanKey}`);
    return null; // Not found in any handler
  }

  /**
   * Save translation to all storage handlers that come before the source handler
   */
  private async saveToStorageHandlers(
    request: TranslationRequest,
    result: TranslationResult,
    sourceHandler: IAsyncHandler
  ): Promise<void> {
    const sourceIndex = this.asyncHandlers.indexOf(sourceHandler);

    // Get all storage handlers with higher priority (lower index) than source
    const storageHandlers = this.asyncHandlers
      .slice(0, sourceIndex)
      .filter(h => h.type === 'storage');

    if (storageHandlers.length === 0) return;

    logger.debug(`Saving to ${storageHandlers.length} storage handlers`);

    // Result already contains value and styles from source handler
    const savePromises = storageHandlers.map(async handler => {
      try {
        await handler.save(request, result);
        logger.debug(`Saved to storage: ${handler.name}`);
      } catch (error) {
        logger.warn(`Failed to save to storage ${handler.name}:`, error);
        const repoBus = this.bus.repo(request, handler.name);
        repoBus.error(error as Error);
      }
    });

    await Promise.allSettled(savePromises);
  }

  private async saveNamespaceToStorageHandlers(
    translations: TranslationData,
    context: TranslationContext
  ): Promise<void> {
    const storageHandlers = this.asyncHandlers.filter(
      h => h.type === 'storage' && h.saveNamespace
    );

    if (storageHandlers.length === 0) return;

    logger.debug(
      `Saving namespace to ${storageHandlers.length} storage handlers`
    );

    // ✅ Save to storage handlers (async/parallel)
    const savePromises = storageHandlers.map(async handler => {
      try {
        await handler.saveNamespace!(translations, context);
        logger.debug(`Namespace saved to storage: ${handler.name}`, {
          locale: context.locale,
          namespace: context.namespace,
        });
      } catch (error) {
        logger.warn(
          `Failed to save namespace to storage ${handler.name}:`,
          error
        );
        const repoBus = this.bus.repo(
          `${context.locale}:${context.namespace}`,
          handler.name
        );
        repoBus.error(error as Error);
      }
    });

    await Promise.allSettled(savePromises);
  }

  async saveNamespaceToHandlers(
    translations: TranslationData,
    context: TranslationContext
  ): Promise<void> {
    this.saveNamespaceToMemoryHandlers(translations, context);
    await this.saveNamespaceToStorageHandlers(translations, context);
  }

  getSyncHandlers(): ISyncHandler[] {
    return [...this.syncHandlers];
  }

  getAsyncHandlers(): IAsyncHandler[] {
    return [...this.asyncHandlers];
  }

  /**
   * Get chain info for debugging
   */
  getChainInfo(): {
    length: number;
    handlers: Array<{
      name: string;
      priority: number;
      type: 'memory' | 'storage' | 'network' | 'fallback';
      enabled: boolean | undefined;
    }>;
  } {
    return {
      length: this.syncHandlers.length + this.asyncHandlers.length,
      handlers: [
        ...this.syncHandlers.map(h => ({
          name: h.name,
          priority: h.priority,
          type: h.type,
          enabled: h.enabled,
        })),
        ...this.asyncHandlers.map(h => ({
          name: h.name,
          priority: h.priority,
          type: h.type,
          enabled: h.enabled,
        })),
      ],
    };
  }

  /**
   * Check if any handler has cached data
   */
  isCached(request: TranslationRequest): boolean {
    return (
      this.syncHandlers.some(h => h.isCached?.(request) === true) ||
      this.asyncHandlers.some(h => h.isCached?.(request) === true)
    );
  }

  /**
   * Get combined cache entries
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEntries(): Record<string, any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entries: Record<string, any> = {};

    // ✅ Fix: Use proper arrays instead of this.handlers
    const allHandlers = [...this.syncHandlers, ...this.asyncHandlers];
    for (const handler of allHandlers) {
      if (handler.getEntries) {
        entries[handler.name] = handler.getEntries();
      }
    }
    return entries;
  }

  /**
   * Destroy all handlers
   */
  async destroy(): Promise<void> {
    // ✅ Fix: Use proper arrays instead of this.handlers
    const allHandlers = [...this.syncHandlers, ...this.asyncHandlers];

    const destroyPromises = allHandlers.map(async handler => {
      try {
        await handler.destroy?.();
        logger.debug(`Handler destroyed: ${handler.name}`);
      } catch (error) {
        logger.warn(`Failed to destroy handler: ${handler.name}`, error);
      }
    });

    await Promise.allSettled(destroyPromises);

    // Clear handler arrays
    this.syncHandlers = [];
    this.asyncHandlers = [];
  }

  private sortHandlersIfNeeded(
    handlers: ISyncHandler[] | IAsyncHandler[]
  ): void {
    const hasExplicitPriorities = handlers.some(h => h.priority > 0);

    if (hasExplicitPriorities) {
      handlers.sort((a, b) => {
        const priorityA = a.priority || Number.MAX_SAFE_INTEGER;
        const priorityB = b.priority || Number.MAX_SAFE_INTEGER;
        return priorityA - priorityB;
      });
    }
  }

  async loadNamespace(
    locale: string,
    namespace: string
  ): Promise<TranslationData | null> {
    for (const handler of this.asyncHandlers) {
      if (handler.type !== 'network') continue;
      try {
        logger.debug(
          `Trying to load namespace: ${locale}:${namespace} from ${handler.name}`
        );
        const result = await handler.loadNamespace?.(locale, namespace);
        if (result && result.data && result.status === 200) {
          logger.debug(
            `Loaded namespace: ${locale}:${namespace} from ${handler.name}`
          );
          await this.saveNamespaceToStorageHandlers(result.data, {
            namespace,
            locale,
          });
          this.saveNamespaceToMemoryHandlers(result.data, {
            namespace,
            locale,
          });
          return result.data;
        }
      } catch (error) {
        logger.warn(
          `Failed to load namespace: ${locale}:${namespace} from ${handler.name}`,
          error
        );
        const repoBus = this.bus.repo(`${locale}:${namespace}`, handler.name);
        repoBus.error(error as Error);
        continue;
      }
    }
    logger.debug(`No handlers found namespace: ${locale}:${namespace}`);
    return null;
  }

  clearCache(): void {
    const allHandlers = [...this.syncHandlers, ...this.asyncHandlers];

    for (const handler of allHandlers) {
      try {
        handler.destroy?.();
        logger.debug(`Cache cleared for handler: ${handler.name}`);
      } catch (error) {
        logger.warn(
          `Failed to clear cache for handler: ${handler.name}`,
          error
        );
      }
    }

    logger.debug('Cache cleared for all handlers');
  }

  /**
   * Clear cache entries for a specific namespace
   */
  clearNamespace(locale: string, namespace: string): void {
    // Clear from sync handlers (memory)
    for (const handler of this.syncHandlers) {
      try {
        handler.clearNamespace?.(locale, namespace);
      } catch (error) {
        logger.warn(
          `Failed to clear namespace for handler: ${handler.name}`,
          error
        );
      }
    }

    logger.debug(`Cleared namespace: ${locale}:${namespace}`);
  }

  /**
   * Set a value directly in the cache (for optimistic updates)
   * Bypasses the normal handler flow to directly set values in memory handlers
   * Stores as ContentUnit (text + styles) for consistency
   *
   * @param key - Full cache key (e.g., 'en:app:welcome')
   * @param value - Translation value
   * @param styles - Optional styles for indexed tags
   */
  setCache(key: string, value: string, styles?: Record<string, string[]>): void {
    // Save to memory handlers using the dedicated setCache method
    // Handler stores as ContentUnit JSON internally
    for (const handler of this.syncHandlers) {
      if (handler.type === 'memory' && handler.setCache) {
        try {
          handler.setCache(key, value, styles);
        } catch (error) {
          logger.warn(`Failed to set cache in handler: ${handler.name}`, error);
        }
      }
    }

    logger.debug(`Cache set: ${key}`);
  }

  /**
   * Get namespace data from cache for ContentUnit style lookup
   *
   * Used by t() to retrieve styles for indexed tags at render time.
   *
   * @param namespaceCacheKey - Cache key in format @locale:namespace
   * @returns Parsed namespace data or null if not found
   */
  getNamespaceData(namespaceCacheKey: string): TranslationData | null {
    // Try to get from memory handlers first (sync)
    for (const handler of this.syncHandlers) {
      if (handler.type === 'memory') {
        try {
          const entries = handler.getEntries?.();
          if (entries && entries[namespaceCacheKey]) {
            const data = entries[namespaceCacheKey];
            // Parse if it's a string (JSON)
            if (typeof data === 'string') {
              return JSON.parse(data);
            }
            return data;
          }
        } catch (error) {
          logger.debug(`Error getting namespace data from ${handler.name}:`, error);
        }
      }
    }
    return null;
  }
}
